import _ from 'lodash';
import { Firebase, FirebaseForUserCreation, callIfFunction, unwrapDoc, logAndDispatchErrorForAction, withoutId, generateSafePassword, getCurrentTimestamp, accountId, organizationId, isProduction, omitDeleted, addKeysAsIds, appConfig } from './../util';
import { OrganizationRef, UsersRef } from './../util/refs';

import {
  BEGIN_EDITING_USER,
  SAVE_USER,
  SAVE_USER_SUCCESS,
  SAVE_USER_ERROR,
  INVITE_USER,
  INVITE_USER_SUCCESS,
  INVITE_USER_ERROR,
  END_EDITING_USER,
  VIEW_USER,
  VIEW_USER_SUCCESS,
  VIEW_USER_ERROR,
} from './types';

export function unloadUser(callback){
  return dispatch => {
    dispatch({
      type: VIEW_USER_SUCCESS,
      payload: null
    });
  }
}

export function fetchUser(userId, callback){
  return dispatch => {

    dispatch({
      type: VIEW_USER,
    });

    UsersRef().doc(userId).get().then(doc => {
      const user = unwrapDoc(doc);

      dispatch({
        type: VIEW_USER_SUCCESS,
        payload: user
      });

      callIfFunction(callback);
    }).catch(err => { logAndDispatchErrorForAction(`Fetch user data for ${userId}`, err, dispatch, VIEW_USER_ERROR, callback); });
  }
}

export function beginEditingUser(userData){
  return {
    type: BEGIN_EDITING_USER,
    payload: userData
  }
}

export function stopEditingUser(){
  return {
    type: END_EDITING_USER
  }
}

export function createOrUpdateUser(userData){
  if(userData._id){
    return updateUser(userData);
  }
  else{
    return createUser(userData);
  }
}

function resetUserPasswordForUserWithEmail(email){
  FirebaseForUserCreation.auth().sendPasswordResetEmail(email, {
    url: `${appConfig().APP_URL}/login?email=${encodeURIComponent(email)}`
  });
}

function resetPasswordAndSendWelcomeEmailForUser(userId, callback){
  OrganizationRef().collection('users').doc(userId).update({
    sendWelcomeEmail: true
  }).then(() => {
    UsersRef().doc(userId).get().then(doc => {

      console.log('resetting password for user: ', unwrapDoc(doc));
      const { email } = unwrapDoc(doc);
      resetUserPasswordForUserWithEmail(email);

      callIfFunction(callback);
    }).catch(err => {
      callIfFunction(callback, err);
    });
  });
}

export function resendUserInvitation(userId){

  console.log('resending user invitation');

  return dispatch => {
    dispatch({
      type: INVITE_USER
    });

    resetPasswordAndSendWelcomeEmailForUser(userId, err => {
      if(err){
        return logAndDispatchErrorForAction('Reset password and send welcome email for user', err, dispatch, INVITE_USER_ERROR);
      }
      else{
        dispatch({
          type: INVITE_USER_SUCCESS,
          payload: 'Email invitation sent'
        });
      }
    });
  }
}

export function importUser(email, callback){
  const callable = Firebase.functions().httpsCallable('adminImportUserToOrganizationFromPublic');

  callable({ email }).then(result => {
    callback(result.data);
  }).catch(err => {
    callback({
      error: err.message
    });
  });
}

export function createUser(userData, sendWelcomeEmail = false){
  console.log('creating user!');

  if(userData.salesforceId && (!userData.salesforceId || userData.salesforceId === '')){
    delete userData.salesforceId;
  }

  return dispatch => {

    dispatch({
      type: SAVE_USER,
      payload: userData,
    });

    let newUser = {
      ...userData,
      organizationId: organizationId(),
      createdOn: getCurrentTimestamp(),
      lastUpdated: getCurrentTimestamp(),
    }

    //welcome email will not be sent by firebase function if not set
    if(sendWelcomeEmail){
      newUser.sendWelcomeEmail = true;
    }

    //create user in authentication database - using firebase app specifically for user creation so we don't sign out
    //https://stackoverflow.com/questions/37517208/firebase-kicks-out-current-user/38013551#38013551
    FirebaseForUserCreation.auth().createUserWithEmailAndPassword(userData.email, generateSafePassword()).then(newUserCredential => {

      let user = FirebaseForUserCreation.auth().currentUser;

      user.updateProfile({
        displayName: `${userData.firstName}`
      }).then(() => {
        //sign out in secodary app to prevent unintented operations (probably not necessary)
        FirebaseForUserCreation.auth().signOut();

        //https://firebase.google.com/docs/reference/js/firebase.auth#.UserCredential
        const newUserId = newUserCredential.user.uid;
        newUser._id = newUserId;

        //create user object in db
        UsersRef().doc(newUserId).set(newUser).then(() => {

          console.log('user created with id: ', newUserId);

          //add user to organization
          OrganizationRef().collection('roles').doc(newUserId).set({
            role: userData.role
          }).then(() => {
            if(sendWelcomeEmail){
              resetPasswordAndSendWelcomeEmailForUser(newUserId, err => {
                if(err){
                  return logAndDispatchErrorForAction('Invite newly created user', err, dispatch, SAVE_USER_ERROR)
                }
                else{
                  dispatch({
                    type: SAVE_USER_SUCCESS,
                    payload: {
                      user: newUser,
                      message: `${newUser.firstName} ${newUser.lastName} added to organization & invited`,
                    }
                  });
                }
              });
            }
            else{
              dispatch({
                type: SAVE_USER_SUCCESS,
                payload: {
                  user: newUser,
                  message: `${newUser.firstName} ${newUser.lastName} added to organization`,
                }
              });
            }

          }).catch(err => logAndDispatchErrorForAction('Update user org role for new user', err, dispatch, SAVE_USER_ERROR));

        }).catch(err => logAndDispatchErrorForAction('Create User Object', err, dispatch, SAVE_USER_ERROR));

      }).catch(err => logAndDispatchErrorForAction('Updating user profile after creation', err, dispatch, SAVE_USER_ERROR));

    }).catch(err => logAndDispatchErrorForAction('Create User Authentication', err, dispatch, SAVE_USER_ERROR));
  }
}

export function updateUserWithRole(userData, callback){

  //console.log('updating user!');

  const userId = userData._id;

  if(userData.salesforceId && (!userData.salesforceId || userData.salesforceId === '')){
    delete userData.salesforceId;
  }

  return dispatch => {

    dispatch({
      type: SAVE_USER,
      payload: userData,
    });

    UsersRef().doc(userId).update({
      ...withoutId(userData),
      lastUpdated: getCurrentTimestamp(),
    }).then(() => {

      OrganizationRef().collection('roles').doc(userId).update({
        role: userData.role
      }).then(() => {

        dispatch({
          type: SAVE_USER_SUCCESS,
          payload: {
            user: userData,
            message: 'User updated.',
          }
        });

        callIfFunction(callback);

      }).catch(err => logAndDispatchErrorForAction('Update user org role for new user', err, dispatch, SAVE_USER_ERROR));
    }).catch(err => logAndDispatchErrorForAction('Update user object', err, dispatch, SAVE_USER_ERROR));
  }
}

export function updateUser(userData, callback){

  //console.log('updating user!');

  const userId = userData._id;

  return dispatch => {

    dispatch({
      type: SAVE_USER,
      payload: userData,
    });

    UsersRef().doc(userId).update({
      ...withoutId(userData),
      lastUpdated: getCurrentTimestamp(),
    }).then(() => {

      dispatch({
        type: SAVE_USER_SUCCESS,
        payload: {
          user: userData,
          message: 'User updated.',
        }
      });

      callIfFunction(callback);

    }).catch(err => logAndDispatchErrorForAction('Update user object', err, dispatch, SAVE_USER_ERROR));
  }
}



// WEBPACK FOOTER //
// ./src/actions/users.js